import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { CloudscapeJsonSchema } from "@amzn/fin-tech-cloudscape-json-forms";
import { fetchSchema } from "src/api/schemas/fetchSchema";
import { JsonFormsState, ValidationError, SubmissionState } from "./types";
import type {
  CargoPreScreenData,
  IplPreScreenData,
  PreScreenState,
  WcPreScreenData,
} from "./preScreen/types";

/**
 * Studio folder used for every Nucleus environment when fetching a
 * pre-screen schema. Per Requirement 6.4, dev / beta / gamma / prod all
 * read from `APPROVED`; `DRAFT` and `PENDING_APPROVAL` are out of scope
 * for the pre-screening flow.
 */
const PRE_SCREEN_SCHEMA_FOLDER = "APPROVED" as const;

/**
 * Async thunk that fetches a pre-screen schema by name from the FinTech
 * Cloudscape JSON Forms Studio service.
 *
 * The thunk's abort `signal` (provided automatically by Redux Toolkit on
 * each invocation) is forwarded to `fetchSchema` so that an unmount of
 * `JsonFormsClaimIntakeApp` cancels any in-flight request.
 *
 * On rejection, `resolvedSchemaName` is intentionally left untouched so
 * the user can retry with the same schema name.
 */
export const fetchPreScreenSchema = createAsyncThunk<
  { name: string; schema: CloudscapeJsonSchema },
  string,
  { rejectValue: string }
>(
  "jsonForms/fetchPreScreenSchema",
  async (name, { rejectWithValue, signal }) => {
    try {
      const schema = await fetchSchema(name, PRE_SCREEN_SCHEMA_FOLDER, signal);
      return { name, schema };
    } catch (err) {
      return rejectWithValue(
        err instanceof Error ? err.message : `Failed to fetch ${name}`
      );
    }
  }
);

/**
 * Initial state for the pre-screening sub-state owned by the LOB-agnostic
 * `JsonFormsClaimIntakeApp`. All per-LOB slots default to `null`; the schema-fetch
 * lifecycle starts in `idle`.
 */
export const preScreenInitialState: PreScreenState = {
  wc: null,
  cargo: null,
  ipl: null,
  resolvedSchemaName: null,
  schemaFetchStatus: "idle",
  schemaFetchErrorMessage: null,
  fetchedSchema: null,
};

/**
 * Initial state for the JSON Forms slice.
 * Form data and validation errors are keyed by `{lob}_{stage}`
 * (e.g., "cargo_stage1") to support multiple LOBs without collisions.
 *
 * This slice is completely separate from the existing `formsSlice`
 * and does not share any state keys with it.
 */
export const initialState: JsonFormsState = {
  formData: {},
  validationErrors: {},
  submission: {
    status: "idle",
    response: null,
    error: null,
  },
  preScreen: preScreenInitialState,
};

export const jsonFormsSlice = createSlice({
  name: "jsonForms",
  initialState,
  reducers: {
    setFormData: (
      state,
      action: PayloadAction<{ key: string; data: Record<string, unknown> }>
    ) => {
      const { key, data } = action.payload;
      state.formData[key] = data;
    },
    setValidationErrors: (
      state,
      action: PayloadAction<{ key: string; errors: ValidationError[] }>
    ) => {
      const { key, errors } = action.payload;
      state.validationErrors[key] = errors;
    },
    setSubmissionStatus: (state, action: PayloadAction<SubmissionState>) => {
      state.submission = action.payload;
    },
    resetFormData: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload;
      delete state.formData[key];
    },
    resetAll: () => {
      return initialState;
    },
    setWcPreScreenData: (
      state,
      action: PayloadAction<WcPreScreenData | null>
    ) => {
      state.preScreen.wc = action.payload;
    },
    setCargoPreScreenData: (
      state,
      action: PayloadAction<CargoPreScreenData | null>
    ) => {
      state.preScreen.cargo = action.payload;
    },
    setIplPreScreenData: (
      state,
      action: PayloadAction<IplPreScreenData | null>
    ) => {
      state.preScreen.ipl = action.payload;
    },
    setResolvedSchemaName: (state, action: PayloadAction<string | null>) => {
      state.preScreen.resolvedSchemaName = action.payload;
    },
    resetPreScreen: (state) => {
      state.preScreen = preScreenInitialState;
    },
    /**
     * Clears the schema-fetch lifecycle (status, error message, fetched
     * schema, resolved name) but leaves the captured per-LOB pre-screen
     * data intact. Used by the schema-fetch error path to send the user
     * back to the pre-screen without losing what they already typed.
     */
    clearSchemaFetchState: (state) => {
      state.preScreen.schemaFetchStatus = "idle";
      state.preScreen.schemaFetchErrorMessage = null;
      state.preScreen.fetchedSchema = null;
      state.preScreen.resolvedSchemaName = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPreScreenSchema.pending, (state) => {
        state.preScreen.schemaFetchStatus = "loading";
        state.preScreen.schemaFetchErrorMessage = null;
      })
      .addCase(fetchPreScreenSchema.fulfilled, (state, action) => {
        state.preScreen.schemaFetchStatus = "success";
        state.preScreen.fetchedSchema = action.payload.schema;
        state.preScreen.resolvedSchemaName = action.payload.name;
        state.preScreen.schemaFetchErrorMessage = null;
      })
      .addCase(fetchPreScreenSchema.rejected, (state, action) => {
        state.preScreen.schemaFetchStatus = "error";
        state.preScreen.schemaFetchErrorMessage =
          action.payload ?? action.error.message ?? null;
      });
  },
});

export const {
  setFormData,
  setValidationErrors,
  setSubmissionStatus,
  resetFormData,
  resetAll,
  setWcPreScreenData,
  setCargoPreScreenData,
  setIplPreScreenData,
  setResolvedSchemaName,
  resetPreScreen,
  clearSchemaFetchState,
} = jsonFormsSlice.actions;

export default jsonFormsSlice.reducer;
